import {
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  MRT_ExpandAllButton,
  MRT_ExpandedState,
  MRT_Row,
  type MRT_RowData,
  MRT_SortingState,
  MRT_TableInstance,
  type MRT_TableOptions,
  MRT_Updater,
  MRT_VisibilityState,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table"
import { download, generateCsv, mkConfig } from "export-to-csv"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import { Box, Button, Stack } from "@mui/material"
import { useCallback } from "react"
import { useSessionStorage } from "usehooks-ts"

const defaultCsvConfig = {
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
}

export interface ExportTransformOutput {
  data: Record<string, unknown>[]
  csvConfig?: {
    filename: string
  }
}

export type TableState = {
  columnFilters: MRT_ColumnFiltersState
  columnVisibility: MRT_VisibilityState
  density: MRT_DensityState
  globalFilter: string | undefined
  showColumnFilters: boolean
  showGlobalFilter: boolean
  sorting: MRT_SortingState
  expanded: MRT_ExpandedState
}

export const defaultTableState: TableState = {
  columnFilters: [],
  columnVisibility: {},
  density: "compact",
  globalFilter: undefined,
  showColumnFilters: false,
  showGlobalFilter: false,
  sorting: [],
  expanded: {},
}

export const tableStateSuffix = "_tableState"

interface TableProps<TData extends MRT_RowData> extends MRT_TableOptions<TData> {
  export_label?: string
  columns: MRT_ColumnDef<TData>[]
  data: TData[]
  isExportable?: boolean
  isExportableRows?: boolean
  isExportableSelection?: boolean
  exportTransformFn?: (data: TData[]) => ExportTransformOutput
  sessionStorageName: string
}

export const Table = <TData extends MRT_RowData>({
  export_label,
  columns,
  data,
  isExportable = false,
  isExportableRows = true,
  isExportableSelection = true,
  sessionStorageName,
  enableExpandAll = true,
  exportTransformFn,
  ...rest
}: TableProps<TData>) => {
  // https://v2.material-react-table.com/docs/guides/pagination#customize-pagination-behavior
  // "This table option is true by default, and causes a table to automatically reset the table back to the first page whenever sorting, filtering, or grouping occurs."
  //
  // disable reseting page if there is expanable row area. No idea why, but seems that expanding row also resets page to first one.
  // TODO kk: check if still the case in in newer material-react-table version
  const autoResetPageIndex = !rest.renderDetailPanel

  const handleExportRows = (rows: MRT_Row<TData>[]) => {
    const rowData = rows.map((row) => row.original)
    handleExportData(rowData)
  }

  const handleExportData = (data: TData[]) => {
    if (exportTransformFn) {
      const output = exportTransformFn(data)
      let config = { ...defaultCsvConfig }
      if (output.csvConfig) {
        config = { ...config, ...output.csvConfig }
      }
      config = mkConfig(config)
      const csv = generateCsv(config)(output.data)
      download(config)(csv)
    } else {
      const config = mkConfig(defaultCsvConfig)
      const csv = generateCsv(config)(data)
      download(config)(csv)
    }
  }

  const getExportedToolbarButtons = useCallback(
    (table: MRT_TableInstance<TData>) => (
      <>
        {isExportable && (
          <Button
            size="small"
            //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
            onClick={() => handleExportData(data)}
            startIcon={<FileDownloadIcon />}
          >
            {export_label ? export_label : "Export All Data"}
          </Button>
        )}
        {isExportableRows && (
          <Button
            size="small"
            disabled={table.getPrePaginationRowModel().rows.length === 0}
            //export all rows, including from the next page, (still respects filtering and sorting)
            onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Export All Rows
          </Button>
        )}
        {isExportableSelection && (
          <Button
            size="small"
            disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            //only export selected rows
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Export Selected Data
          </Button>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
    [handleExportData, handleExportRows],
  )

  const [tableState, setTableState] = useSessionStorage<TableState>(
    `${sessionStorageName}${tableStateSuffix}`,
    {
      ...defaultTableState,
      ...rest.state,
    },
  )

  const handleColumnFiltersChange = (updater: MRT_Updater<MRT_ColumnFiltersState>) => {
    if (rest.onColumnFiltersChange) {
      rest.onColumnFiltersChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.columnFilters) : updater
      return {
        ...prevState,
        columnFilters: newState,
      }
    })
  }

  const handleColumnVisibilityChange = (updater: MRT_Updater<MRT_VisibilityState>) => {
    if (rest.onColumnVisibilityChange) {
      rest.onColumnVisibilityChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.columnVisibility) : updater
      return {
        ...prevState,
        columnVisibility: newState,
      }
    })
  }

  const handleDensityChange = (updater: MRT_Updater<MRT_DensityState>) => {
    if (rest.onDensityChange) {
      rest.onDensityChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.density) : updater
      return {
        ...prevState,
        density: newState,
      }
    })
  }

  const handleGlobalFilterChange = (updater: MRT_Updater<string | undefined>) => {
    if (rest.onGlobalFilterChange) {
      rest.onGlobalFilterChange((prevState: string | undefined) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.globalFilter) : updater
      return {
        ...prevState,
        globalFilter: newState,
      }
    })
  }

  const handleShowColumnFiltersChange = (updater: MRT_Updater<boolean>) => {
    if (rest.onShowColumnFiltersChange) {
      rest.onShowColumnFiltersChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.showColumnFilters) : updater
      return {
        ...prevState,
        showColumnFilters: newState,
      }
    })
  }

  const handleShowGlobalFilterChange = (updater: MRT_Updater<boolean>) => {
    if (rest.onShowGlobalFilterChange) {
      rest.onShowGlobalFilterChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.showGlobalFilter) : updater
      return {
        ...prevState,
        showGlobalFilter: newState,
      }
    })
  }

  const handleSortingChange = (updater: MRT_Updater<MRT_SortingState>) => {
    if (rest.onSortingChange) {
      rest.onSortingChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.sorting) : updater
      return {
        ...prevState,
        sorting: newState,
      }
    })
  }

  const handleExpandChange = (updater: MRT_Updater<MRT_ExpandedState>) => {
    if (rest.onExpandedChange) {
      rest.onExpandedChange((prevState) =>
        updater instanceof Function ? updater(prevState) : updater,
      )
    }

    setTableState((prevState) => {
      const newState = updater instanceof Function ? updater(prevState.expanded) : updater
      return {
        ...prevState,
        expanded: newState,
      }
    })
  }

  const table = useMaterialReactTable({
    columns,
    data,
    muiTableHeadCellProps: {
      sx: {
        "&.MuiTableCell-head": {
          background: "#e8eae8",
          fontWeight: "bold",
        },
      },
    },
    muiDetailPanelProps: () => ({
      sx: {
        backgroundColor: "rgba(225, 225, 225, 0.8)",
        "& .MuiCollapse-root": {
          width: "100%",
        },
      },
    }),
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Header: enableExpandAll
          ? () => (
              <Stack direction="row" alignItems="center">
                <MRT_ExpandAllButton table={table} />
                <Box>Groups</Box>
              </Stack>
            )
          : () => <></>,
        GroupedCell: ({ row, table }) => {
          const { grouping } = table.getState()
          const val = grouping[grouping.length - 1]
          if (!val) {
            return undefined
          }
          return row.getValue(val)
        },
        grow: false,
      },
    },
    enableColumnResizing: true,
    layoutMode: "grid",
    positionActionsColumn: "last",
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          padding: "0 4px",
          alignItems: "center",
          flexWrap: "wrap",
          alignSelf: "center",
        }}
      >
        {isExportable && getExportedToolbarButtons(table)}
      </Box>
    ),
    autoResetPageIndex,
    ...rest,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onDensityChange: handleDensityChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onShowColumnFiltersChange: handleShowColumnFiltersChange,
    onShowGlobalFilterChange: handleShowGlobalFilterChange,
    onSortingChange: handleSortingChange,
    onExpandedChange: handleExpandChange,
    state: { ...tableState, ...rest.state },
  })

  return <MaterialReactTable table={table} />
}
